import { useId } from "react";
import { Radio as BaseUIRadio, RadioRootProps } from "@base-ui/react/radio";
import { RadioGroup as BaseUIRadioGroup, RadioGroupProps as BaseUIRadioGroupProps } from "@base-ui/react/radio-group";
import { cn } from "@/lib/utils";
import Label from "../label";
import "./style.scss";

interface RadioProps extends RadioRootProps {
	label?: React.ReactNode;
}

export const Radio: React.FC<RadioProps> = ({ label, ...props }) => {
	return (
		<label className={cn("flex items-center gap-2 text-base cursor-pointer group has-disabled:cursor-not-allowed has-data-disabled:cursor-not-allowed")}>
			<BaseUIRadio.Root
				{...props}
				className="ms-radio p-0 transition border flex size-5 shrink-0 items-center justify-center rounded-full data-unchecked:group-hover:outline-1 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-60"
			>
				<BaseUIRadio.Indicator className="flex size-2.5 rounded-full bg-current data-unchecked:hidden" />
			</BaseUIRadio.Root>

			<span>{label}</span>
		</label>
	);
};

export const RadioCard: React.FC<RadioProps & { description?: string }> = ({ label, description, ...props }) => {
	return (
		<label className={cn("ms-radio-card flex flex-col gap-2 w-full border transition relative")}>
			<BaseUIRadio.Root
				{...props}
				className={cn(
					"ms-radio absolute flex size-5 shrink-0 items-center justify-center rounded-full border p-0 transition",
					"focus-visible:outline-2 focus-visible:outline-offset-2",
					"disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60",
					"data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-60",
					"data-unchecked:group-hover:outline-1",
				)}
			>
				<BaseUIRadio.Indicator className="flex size-2.5 rounded-full bg-current data-unchecked:hidden" />
			</BaseUIRadio.Root>

			<strong>{label}</strong>
			<span className="text-sm">{description}</span>
		</label>
	);
};

interface RadioGroupProps extends BaseUIRadioGroupProps {
	label?: string;
	required?: boolean;
	name?: string;
	radiosContainerClassName?: string;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({ children, label, required, radiosContainerClassName, ...props }) => {
	const generatedId = useId();
	const id = props?.id ?? generatedId;

	return (
		<BaseUIRadioGroup {...props} aria-labelledby={id} className={cn("flex flex-col items-start gap-2", props.className)}>
			<Label variant="legend" id={id} required={required}>
				{label}
			</Label>
			<div className={cn("flex flex-col gap-1 w-full", radiosContainerClassName)}>{children}</div>
		</BaseUIRadioGroup>
	);
};
